import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import { User, UserLevel } from '@infuse/iam/user/type';
import { getLevel } from '@infuse/iam/user/utils';

// currentUserStore is dynamically imported to avoid circular dependency
// eslint-disable-next-line @nx/enforce-module-boundaries
import { currentUser } from '@infuse/api/iam/current-user';

interface CurrentUserState {
  permissions: { permissionId: string; subscriptionId: string }[];
  user?: User;
  level: UserLevel;
  mainSubscriptionId?: string;
  enabled: boolean;
}

export const useCurrentUserStore = defineStore('currentUser', {
  state: (): CurrentUserState => ({
    permissions: [],
    level: UserLevel.DOMAIN,
    enabled: true,
    mainSubscriptionId: undefined
  }),
  actions: {
    async fetchCurrentUser() {
      try {
        this.enabled = true;
        const fetchedUser = await currentUser();

        this.permissions = fetchedUser.permissions;
        this.user = fetchedUser.user;
        this.level = getLevel(this.user);
      } catch (e) {
        // IAM is enabled, but no user found, allowed to fail
        // User is redirected to "Not allowed page" by guard
      }
    },
    setSentryScope() {
      if (!this.user || !Sentry.isInitialized()) {
        return;
      }

      Sentry.getCurrentScope().setTag('level', this.level);

      if (this.user.organisationId) {
        Sentry.getCurrentScope().setTag('organisationId', this.user.organisationId);
      }
      if (this.user.tenantId) {
        Sentry.getCurrentScope().setTag('tenantId', this.user.tenantId);
      }
    },
    disable() {
      this.enabled = false;
    },
    reset() {
      this.permissions = [];
    },
    setMainSubscriptionId(subscriptionId: string) {
      this.mainSubscriptionId = subscriptionId;
    },
    hasPermission(id: string, subscriptionId?: string): boolean {
      const { VITE_LOG_PERMISSIONS = false } = (import.meta as unknown as { env: Record<string, string> }).env;

      if (VITE_LOG_PERMISSIONS) {
        console.log('Permission check for:', id);
      }

      if (subscriptionId) {
        return this.permissions.some((p) => p.permissionId === id && p.subscriptionId === subscriptionId);
      } else {
        return this.permissions.some((p) => p.permissionId === id);
      }
    },
    hasPermissions(ids: string[], subscriptionId?: string): boolean {
      return ids.every((id) => this.hasPermission(id, subscriptionId));
    }
  }
});
